import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HOSPITAL_CLAIM_ENABLED } from './featureFlags';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [summary, setSummary] = useState({
    pendingCorrections: 0,
    reviewedCorrections: 0,
    rejectedCorrections: 0,
    pendingHospitalClaims: 0,
    approvedHospitalClaims: 0,
    totalUsers: 0,
    adminUsers: 0
  });
  const [error, setError] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [funnelDays, setFunnelDays] = useState(14);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await fetch('/api/admin/summary', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`
          }
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Unable to load admin summary.');
        }

        setSummary(data);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchSummary();
  }, []);

  useEffect(() => {
    const fetchFunnel = async () => {
      try {
        const response = await fetch(`/api/admin/funnel?days=${funnelDays}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`
          }
        });
        const data = await response.json();
        if (response.ok) {
          setFunnel(data);
        }
      } catch (err) {
        // Funnel is a nice-to-have; don't block the dashboard on it.
      }
    };

    fetchFunnel();
  }, [funnelDays]);

  return (
    <main className="admin-dashboard-page">
      <section className="admin-dashboard-shell">
        <div className="admin-dashboard-header">
          <p>Admin</p>
          <h1>VetBill Admin</h1>
          <span>Manage review queues, content, and site tools from one place.</span>
        </div>

        {error && <div className="admin-dashboard-error">{error}</div>}

        {funnel && (
          <div className="admin-funnel">
            <div className="admin-funnel-header">
              <h2>Upload funnel</h2>
              <div className="admin-funnel-range">
                {[7, 14, 30].map(days => (
                  <button
                    key={days}
                    type="button"
                    className={funnelDays === days ? 'active' : ''}
                    onClick={() => setFunnelDays(days)}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
            {(!funnel.funnel || funnel.funnel[0]?.attempts === 0) ? (
              <p className="admin-funnel-empty">No upload attempts in the last {funnel.days} days yet.</p>
            ) : (
              <>
                <ul className="admin-funnel-steps">
                  {funnel.funnel.map((step, index) => {
                    const prev = index > 0 ? funnel.funnel[index - 1].attempts : step.attempts;
                    const dropFromPrev = prev > 0 ? Math.round(((prev - step.attempts) / prev) * 100) : 0;
                    return (
                      <li key={step.event}>
                        <div className="admin-funnel-bar-row">
                          <span className="admin-funnel-label">{step.label}</span>
                          <div className="admin-funnel-bar-track">
                            <div className="admin-funnel-bar" style={{ width: `${step.conversionFromStart}%` }} />
                          </div>
                          <span className="admin-funnel-count">
                            {step.attempts}
                            <em>{step.conversionFromStart}%</em>
                          </span>
                        </div>
                        {index > 0 && dropFromPrev > 0 && (
                          <span className="admin-funnel-drop">↓ {dropFromPrev}% dropped off here</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {funnel.sideEvents?.some(s => s.count > 0) && (
                  <div className="admin-funnel-side">
                    {funnel.sideEvents.filter(s => s.count > 0).map(s => (
                      <span key={s.event}>{s.label}: <strong>{s.count}</strong></span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="admin-metrics-grid">
          <div>
            <span>Pending corrections</span>
            <strong>{summary.pendingCorrections}</strong>
          </div>
          <div>
            <span>Reviewed</span>
            <strong>{summary.reviewedCorrections}</strong>
          </div>
          <div>
            <span>Rejected</span>
            <strong>{summary.rejectedCorrections}</strong>
          </div>
          <div>
            <span>Hospital claims</span>
            <strong>{summary.pendingHospitalClaims}</strong>
          </div>
          <div>
            <span>Total users</span>
            <strong>{summary.totalUsers}</strong>
          </div>
        </div>

        <div className="admin-card-grid">
          <Link to="/admin/corrections" className="admin-card primary">
            <div>
              <h2>Corrections Queue</h2>
              <p>Review user-submitted correction and support reports.</p>
            </div>
            <span>{summary.pendingCorrections} pending</span>
          </Link>

          <Link to="/addpost" className="admin-card">
            <div>
              <h2>Add Advice Post</h2>
              <p>Create educational content for the Advice section.</p>
            </div>
            <span>Content</span>
          </Link>

          <Link to="/admin/users" className="admin-card">
            <div>
              <h2>User Roles</h2>
              <p>Search users and manage admin access.</p>
            </div>
            <span>{summary.adminUsers} admins</span>
          </Link>

          <Link to="/admin/uploads" className="admin-card">
            <div>
              <h2>Check uploads for personal info</h2>
              <p>Find uploads that still show personal info and hide anything users missed.</p>
            </div>
            <span>{summary.unredactedUploads || 0} to review</span>
          </Link>

          {HOSPITAL_CLAIM_ENABLED && (
            <Link to="/admin/hospital-claims" className="admin-card">
              <div>
                <h2>Vet Partners</h2>
                <p>Review hospital profile claim requests and verified profile details.</p>
              </div>
              <span>{summary.pendingHospitalClaims} pending</span>
            </Link>
          )}
        </div>
      </section>
    </main>
  );
};

export default AdminDashboard;
